import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { v4 as uuid } from 'uuid';
import apis from '@src/apis';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import { Avatar, Box, Grid } from '@mui/material';
import { ArrowBack, Edit, WarningRounded } from '@mui/icons-material';
import {
  SUB_STATUS,
  USERNAME_EXISTS,
  VOICE_LOCALES,
  VOICE_NAME_REGEX,
  VOICE_STATUS,
} from '@src/constants/voiceCloning';
import { getCategoryLabel } from '@src/services/voiceCloning';
import ArrowRightRoundedIcon from '@mui/icons-material/ArrowRightRounded';
import actions from '@src/redux/actions';
import CommonButton from '@src/new-components/Button';
import CustomTypography from '@src/new-components/Typography';
import Select from '@src/new-components/Select';
import ButtonLabel from '@src/new-components/Button/ButtonLabel';
import { uploadFileToS3 } from '@src/services/upload';
import { GENDER } from '@src/constants/voice';
import LimitedInput from '@src/new-components/Input/LimitedInput';
import CommonInput from '@src/new-components/Input/CommonInput';
import AlertMessage from '@src/new-components/AlertMessage';
import { COLOR } from '@src/styles/color';
import useAgeGroupVoiceCloning from '@src/hooks/useAgeGroupVoiceCloning';

import { sortProvinces } from '@src/utils/province';
import { StyledEditVoiceInformation } from './index.style';
import PopupChangeVoice from './PopupChangeVoice';

const MAX_NAME_LENGTH = 25;
const MIN_NAME_LENGTH = 5;
const MAX_DESCRIPTION_LENGTH = 200;

const AvatarEdit = ({ isEditVoice, avatar, handleAvatarChange }) => (
  <Box className="avatar-wrapper">
    <label htmlFor="avatar-input">
      <Avatar
        src={avatar}
        alt="avatar"
        disabled={!isEditVoice}
        className="avatar"
      />
      {!isEditVoice && (
        <>
          <input
            type="file"
            accept="image/*"
            id="avatar-input"
            style={{ display: 'none' }}
            onChange={handleAvatarChange}
          />
          <Box className="edit-icon">
            <Edit className="icon" />
          </Box>
        </>
      )}
    </label>
  </Box>
);

const NewEditVoice = ({
  updateVoice,
  goBack,
  voiceCloningInTTS,
  refreshVoiceCloningInTTS,
}) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  // Redux
  const { voice } = useSelector((state) => state.voiceVoiceCloning);
  const { provinces: provincesStore } = useSelector((state) => state.province);

  const { ageGroup } = useAgeGroupVoiceCloning();

  // State new
  const [voiceInfo, setVoiceInfo] = useState({
    voiceName: '',
    region: '',
    province: '',
    gender: '',
    description: '',
    ageGroup: '',
  });
  const [error, setError] = useState({
    voiceName: false,
    gender: false,
    region: false,
    province: false,
    description: false,
    ageGroup: false,
  });
  const [sampleAudios, setSampleAudios] = useState([]);

  useEffect(() => {
    setVoiceInfo({
      voiceName: voice?.name,
      region: voice?.locale,
      province: voice?.province?.id,
      gender: voice?.gender,
      description: voice?.description?.replace(/\s+/g, ' ').trim(),
      ageGroup: voice?.ageGroup,
    });
  }, [voice]);

  // State
  const [avatar, setAvatar] = useState(voice.avatar);
  const [provinces, setProvinces] = useState([]);
  const [avatarFile, setAvatarFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openPopupSampleAudio, setOpenPopupSampleAudio] = useState(false);
  const [selectedSampleVoice, setSelectedSampleVoice] = useState();
  const [loadingSaveChange, setLoadingSaveChange] = useState(false);
  const [draftSampleVoice, setDraftSampleVoice] = useState(null);

  const disableEditAvatar =
    (voice?.status === VOICE_STATUS.PRIVATE &&
      voice?.subStatus === SUB_STATUS.WAITING_FOR_APPROVAL) ||
    (voice?.status === VOICE_STATUS.PUBLIC &&
      [
        SUB_STATUS.WAITING_FOR_APPROVAL,
        SUB_STATUS.WAITING_FOR_REMOVAL,
      ].includes(voice?.subStatus));
  // New

  const disableVoiceName =
    [VOICE_STATUS.PUBLIC, VOICE_STATUS.SUSPENDED, VOICE_STATUS.FAILED].includes(
      voice?.status,
    ) ||
    (voice?.status === VOICE_STATUS.PRIVATE &&
      [SUB_STATUS.WAITING_FOR_APPROVAL].includes(voice?.subStatus));

  const disableAgeGroup =
    (voice?.status === VOICE_STATUS.PRIVATE &&
      voice?.subStatus === SUB_STATUS.WAITING_FOR_APPROVAL) ||
    (voice?.status === VOICE_STATUS.PUBLIC &&
      voice?.subStatus === SUB_STATUS.WAITING_FOR_APPROVAL) ||
    (voice?.status === VOICE_STATUS.PUBLIC &&
      voice?.subStatus === SUB_STATUS.WAITING_FOR_REMOVAL) ||
    voice?.status === VOICE_STATUS.SUSPENDED ||
    voice?.status === VOICE_STATUS.FAILED;

  const disableGender = voice?.status !== VOICE_STATUS?.RECORDING;

  const allowEditDescription =
    [VOICE_STATUS.PRIVATE, VOICE_STATUS.PUBLIC].includes(voice?.status) &&
    (voice?.subStatus === SUB_STATUS.REJECTED || !voice?.subStatus);

  const showDescription =
    voice?.status === VOICE_STATUS.PRIVATE ||
    voice?.status === VOICE_STATUS.PUBLIC;

  const showSampleAudio = [VOICE_STATUS.PRIVATE, VOICE_STATUS.PUBLIC].includes(
    voice?.status,
  );
  const disableSampleAudio =
    (voice?.status === VOICE_STATUS.PRIVATE &&
      voice?.subStatus === SUB_STATUS.WAITING_FOR_APPROVAL) ||
    (voice?.status === VOICE_STATUS.PUBLIC &&
      [
        SUB_STATUS.WAITING_FOR_REMOVAL,
        SUB_STATUS.WAITING_FOR_APPROVAL,
      ].includes(voice?.subStatus));

  const showWarningEditVoiceInfo =
    voice?.status === VOICE_STATUS.PUBLIC &&
    voice?.subStatus === SUB_STATUS.WAITING_FOR_REMOVAL;
  const showWarningEditVoicePending =
    [VOICE_STATUS.PUBLIC, VOICE_STATUS.PRIVATE].includes(voice?.status) &&
    voice?.subStatus === SUB_STATUS.WAITING_FOR_APPROVAL;

  const showNoteWarning =
    voice?.status === VOICE_STATUS.PUBLIC &&
    voice?.subStatus === SUB_STATUS.REJECTED;

  useEffect(() => {
    const sampleAudiosFromTTS = voiceCloningInTTS?.sampleAudios || [];
    const sampleAudiosFromVoiceCloning = voice?.sampleVoices || [];

    const mergedSampleAudios = [
      ...sampleAudiosFromVoiceCloning,
      ...sampleAudiosFromTTS,
    ];
    const formattedSampleAudios = mergedSampleAudios.map((item) => ({
      ...item,
      audioLink: item?.audioLink || item?.sampleLink,
      text: item?.text || item?.sampleText,
    }));
    setSampleAudios(formattedSampleAudios);

    const selectedSampleAudio = formattedSampleAudios.find(
      (item) => item.audioLink === voiceCloningInTTS?.demo,
    );
    setSelectedSampleVoice(selectedSampleAudio || formattedSampleAudios[0]);
  }, [voiceCloningInTTS, voice]);

  // Constants
  const handleGoBack = () => {
    goBack();
  };

  const handleAvatarChange = (event) => {
    const file = event.target.files[0];
    setAvatar(URL.createObjectURL(file));
    setAvatarFile(file);
  };

  const showErrorNotification = (message, params = {}) =>
    dispatch(
      actions.noti.push({
        severity: 'error',
        message: t(message, params),
      }),
    );

  const showSuccessNotification = (message) =>
    dispatch(
      actions.noti.push({
        severity: 'success',
        message: t(message),
      }),
    );

  const handleUploadAvatarToS3 = async (fileName, file) => {
    const extension = fileName.split('.')[1];
    const name = fileName.split('.')[0];
    const directory = 'images/voice-cloning';
    const s3Url = await uploadFileToS3(name, directory, extension, file);

    if (!s3Url) {
      showErrorNotification('uploadError');
      return null;
    }

    return s3Url;
  };

  const uploadImageToS3 = async (file) => {
    const avatarName = file.name;
    const ext = avatarName.split('.').pop();
    const imgName = avatarName.slice(0, avatarName.lastIndexOf('.'));
    const link = await handleUploadAvatarToS3(
      `${imgName}_${uuid()}.${ext}`,
      avatarFile,
    );
    return link;
  };

  const handleSaveChange = async () => {
    let avatarUrl = voice.avatar;
    if (avatarFile) {
      avatarUrl = await uploadImageToS3(avatarFile);
      if (!avatarUrl) {
        showErrorNotification('createVoiceFailed');
        return;
      }
    }

    const dataUpdated = {
      name: voiceInfo.voiceName,
      avatar: avatarUrl,
      demoAudioId: selectedSampleVoice?.id,
    };

    // Validate required fields and constraints
    const errors = {};

    if (
      !voiceInfo.ageGroup ||
      !Object.values(ageGroup).includes(voiceInfo.ageGroup)
    ) {
      errors.ageGroup = t('requiredField', { field: t('ageGroup') });
    }

    if (!voiceInfo.voiceName) {
      errors.voiceName = t('requiredField', { field: t('voiceName') });
    } else {
      const normalizedVoiceName = voiceInfo.voiceName.normalize('NFC');
      if (normalizedVoiceName.length < MIN_NAME_LENGTH) {
        errors.voiceName = t('voiceNameDescription');
      } else if (!VOICE_NAME_REGEX.test(normalizedVoiceName)) {
        errors.voiceName = t('voiceNameDescription');
      }
    }

    if (!voiceInfo.description && showDescription) {
      errors.description = t('requiredField', { field: t('description') });
    }

    if (
      voiceInfo.description &&
      voiceInfo.description.length > MAX_DESCRIPTION_LENGTH &&
      showDescription
    ) {
      errors.description = t('errorDescriptionLength', {
        maxLength: MAX_DESCRIPTION_LENGTH,
      });
    }

    if (Object.keys(errors).length > 0) {
      setError({
        ...error,
        ...errors,
      });
      return;
    }

    if (voiceInfo.gender !== voice.gender)
      dataUpdated.gender = voiceInfo.gender;
    if (voiceInfo.locale !== voice.locale)
      dataUpdated.locale = voiceInfo.locale;
    if (voiceInfo.province !== voice.province?.id)
      dataUpdated.provinceId = voiceInfo.province;
    if (voiceInfo.username !== voice.username)
      dataUpdated.username = voiceInfo.username;
    if (voiceInfo?.region !== voice.locale)
      dataUpdated.locale = voiceInfo.region;
    if (voiceInfo.ageGroup !== voice.ageGroup)
      dataUpdated.ageGroup = voiceInfo.ageGroup;
    if (voiceInfo.description !== voice.description)
      dataUpdated.description = voiceInfo.description;
    setLoadingSaveChange(true);
    const response = await apis.voiceCloning.updateVoiceCloningVoice(
      voice.id,
      dataUpdated,
    );
    if (!response?.status) {
      if (response.errorCode === USERNAME_EXISTS)
        showErrorNotification('voiceUsernameExistsError');
      else showErrorNotification('createVoiceFailed');
      return;
    }
    const descriptionUpdated = voiceInfo.description
      ? voiceInfo.description.replace(/\s+/g, ' ').trim()
      : '';
    const updatedVoice = {
      ...voice,
      name: voiceInfo.voiceName,
      avatar: avatarUrl,
      ...dataUpdated,
      province: provinces.find((item) => item.id === voiceInfo.province),
      locale: voiceInfo.region,
      ageGroup: voiceInfo.ageGroup,
      description: descriptionUpdated,
    };

    await refreshVoiceCloningInTTS();
    updateVoice(updatedVoice);
    dispatch(actions.voiceVoiceCloning.addVoice(updatedVoice));
    showSuccessNotification('updateVoiceInformationSuccess');
    setLoadingSaveChange(false);
  };

  const fetchProvinces = async () => {
    const response = await apis.voiceCloning.getVoiceCloningProvinces({
      searchFields: 'name,code,locale',
    });
    if (!response?.status) {
      dispatch(
        actions.noti.push({
          severity: 'error',
          message: 'getProvincesFailed',
        }),
      );
      return;
    }
    const sortedProvinces = sortProvinces(response?.result?.provinces);

    dispatch(actions.province.addProvinces(sortedProvinces));
  };

  useEffect(() => {
    if (!provincesStore.length) fetchProvinces();
  }, []);

  useEffect(() => {
    if (!voiceInfo.region) {
      setProvinces(provincesStore);
      return;
    }
    const filteredProvincesByLocale = provincesStore.filter((item) =>
      item.locale.toLowerCase().includes(voiceInfo.region.toLowerCase()),
    );
    setProvinces(filteredProvincesByLocale);
  }, [voiceInfo.region, provincesStore]);

  const handleSelect = (e) => {
    if (e.target.name === 'voiceName') {
      if (e.target.value.length > MAX_NAME_LENGTH) {
        const value = e.target.value.slice(0, MAX_NAME_LENGTH);
        setVoiceInfo({ ...voiceInfo, voiceName: value });
        setError({ ...error, voiceName: false });
        return;
      }
    }
    setVoiceInfo({ ...voiceInfo, [e.target.name]: e.target.value });
    setError({ ...error, [e.target.name]: false });
  };

  const EditTitle = () => (
    <Box className="edit-title-wrapper">
      <ArrowBack className="icon" onClick={handleGoBack} />
      <CustomTypography variant="body-20-bold" color={COLOR.descriptionGray}>
        {t('voiceInformation')}
      </CustomTypography>
      <Box />
    </Box>
  );

  const handleRequestUpdateVoice = async () => {
    let avatarUrl = voice.avatar;
    if (avatarFile) {
      avatarUrl = await uploadImageToS3(avatarFile);
      if (!avatarUrl) {
        showErrorNotification('errorOccurred');
        return;
      }
    }
    const data = {
      ageGroup: voiceInfo.ageGroup,
      description: voiceInfo.description,
      avatar: avatarUrl,
      demoAudioId: selectedSampleVoice?.id,
    };

    const errors = {};

    if (!voiceInfo.ageGroup) {
      errors.ageGroup = t('requiredField', { field: t('ageGroup') });
    }

    if (!voiceInfo.description) {
      errors.description = t('requiredField', { field: t('description') });
    }

    if (
      voiceInfo.description &&
      voiceInfo.description.length > MAX_DESCRIPTION_LENGTH
    ) {
      errors.description = t('errorDescriptionLength', {
        maxLength: MAX_DESCRIPTION_LENGTH,
      });
    }

    if (Object.keys(errors).length > 0) {
      setError({
        ...error,
        ...errors,
      });
      return;
    }

    setLoading(true);
    const response = await apis.voiceCloning.requestUpdateVoice(voice.id, data);
    if (response?.status) {
      const updatedVoice = {
        ...voice,
        status: VOICE_STATUS.PUBLIC,
        subStatus: SUB_STATUS.WAITING_FOR_APPROVAL,
      };
      dispatch(actions.voiceVoiceCloning.addVoice(updatedVoice));
      dispatch(actions.voiceVoiceCloning.refreshVoicesList(true));
    }
    setLoading(false);
  };

  const handleCancelRequestUpdateVoice = async () => {
    setLoading(true);
    const response = await apis.voiceCloning.cancelRequestUpdateVoice(voice.id);
    if (response?.status) {
      const updatedVoice = {
        ...voice,
        status: VOICE_STATUS.PUBLIC,
        subStatus: SUB_STATUS.EMPTY,
      };
      dispatch(actions.voiceVoiceCloning.addVoice(updatedVoice));
      dispatch(actions.voiceVoiceCloning.refreshVoicesList(true));
    }
    setLoading(false);
  };
  const SaveChangeButton = () => {
    const showBtnRequestUpdate =
      voice?.status === VOICE_STATUS.PUBLIC &&
      ([SUB_STATUS.REJECTED, SUB_STATUS.EMPTY].includes(voice?.subStatus) ||
        !voice?.subStatus);

    const showBtnCancelUpdate =
      voice?.status === VOICE_STATUS.PUBLIC &&
      voice?.subStatus === SUB_STATUS.WAITING_FOR_APPROVAL;

    if (showBtnRequestUpdate) {
      return (
        <CommonButton
          variant="contained"
          textProps={{
            variantText: 'body-14-semibold',
            color: COLOR.indigo.default,
          }}
          customStyle={{
            height: '40px',
            borderRadius: '8px',
            width: '54%',
            margin: '0 auto',
            padding: '8px 24px !important',
          }}
          disabled={loading}
          onClick={handleRequestUpdateVoice}
        >
          {t('sendRequestUpdateInfo')}
        </CommonButton>
      );
    }

    if (showBtnCancelUpdate) {
      return (
        <CommonButton
          variant="containedSmall"
          textProps={{
            variantText: 'body-14-semibold',
            color: COLOR.white,
          }}
          disabled={loading}
          onClick={handleCancelRequestUpdateVoice}
          customStyle={{
            height: '40px',
            borderRadius: '8px',
            width: '54%',
            margin: '0 auto',
            padding: '8px 24px !important',
            backgroundColor: COLOR.indigo.default,
            '&:hover': {
              backgroundColor: COLOR.indigo.default,
              opacity: 0.8,
            },
            '&:disabled': {
              backgroundColor: COLOR.indigo.default,
              opacity: 0.8,
              cursor: 'not-allowed',
              pointerEvents: 'auto',
            },
          }}
        >
          {t('cancelUpdateRequest')}
        </CommonButton>
      );
    }

    const hideBtnSave =
      (voice?.status === VOICE_STATUS.PRIVATE &&
        voice?.subStatus === SUB_STATUS.WAITING_FOR_APPROVAL) ||
      (voice?.status === VOICE_STATUS.PUBLIC &&
        [
          SUB_STATUS.WAITING_FOR_REMOVAL,
          SUB_STATUS.WAITING_FOR_APPROVAL,
        ].includes(voice?.subStatus)) ||
      [VOICE_STATUS.FAILED, VOICE_STATUS.SUSPENDED].includes(voice?.status);

    if (hideBtnSave) {
      return null;
    }

    return (
      <CommonButton
        variant="contained"
        textProps={{
          variantText: 'body-14-semibold',
          color: COLOR.indigo.default,
        }}
        disabled={loadingSaveChange}
        customStyle={{
          height: '40px',
          borderRadius: '8px',
          width: '30%',
          margin: '0 auto',
        }}
        onClick={handleSaveChange}
      >
        {t('saveChange')}
      </CommonButton>
    );
  };
  return (
    <StyledEditVoiceInformation>
      <EditTitle />
      <AvatarEdit
        isEditVoice={disableEditAvatar}
        avatar={avatar}
        handleAvatarChange={handleAvatarChange}
      />
      {(showWarningEditVoiceInfo || showWarningEditVoicePending) && (
        <AlertMessage
          type="warning"
          startIcon={
            <WarningRounded sx={{ fontSize: '20px', color: COLOR.alert }} />
          }
          message={
            showWarningEditVoiceInfo
              ? t('cannotEditVoiceInfo')
              : t('cannotEditVoicePending')
          }
          textProps={{ variant: 'body-14-regular', color: COLOR.darkGray }}
        />
      )}
      {showNoteWarning && voice?.note && (
        <AlertMessage
          type="error"
          startIcon={
            <InfoRoundedIcon
              sx={{ fontSize: '20px', color: COLOR.textRejected }}
            />
          }
          message={voice?.note}
          textProps={{ variant: 'body-14-regular', color: COLOR.darkGray }}
        />
      )}

      <Box className="info-wrapper">
        <Grid container spacing={1} mb={0} mt={0}>
          {showSampleAudio && (
            <Grid item xs={12} sm={12}>
              <ButtonLabel
                customStyle={{
                  height: '40px',
                  borderRadius: '8px',
                  border: `1px solid ${COLOR.borderButtonCorlor}`,
                  width: '100%',
                  justifyContent: 'space-between',
                  padding: '0 16px',
                  alignItems: 'center',
                  gap: '8px',
                  '&:focus': {
                    backgroundColor: 'transparent',
                  },
                  '&:hover': {
                    border: `1px solid ${COLOR.borderButtonCorlor}`,
                    opacity: 1,
                  },
                  backgroundColor: disableSampleAudio
                    ? COLOR.backgroundRecordTime
                    : 'transparent',
                  '&:disabled': {
                    backgroundColor: COLOR.backgroundRecordTime,
                    opacity: 1,
                    cursor: 'not-allowed',
                    pointerEvents: 'auto',
                  },
                }}
                disabled={disableSampleAudio}
                onClick={() => setOpenPopupSampleAudio(true)}
                variant="outlined"
                label={
                  <CustomTypography
                    variant="body-14-medium"
                    color={COLOR.darkGray}
                  >
                    {t('sampleAudio')}
                  </CustomTypography>
                }
              >
                <CustomTypography
                  variant="body-14-regular"
                  color={
                    disableSampleAudio ? COLOR.colorGrayV2 : COLOR.darkGray
                  }
                >
                  Audio{' '}
                  {sampleAudios?.findIndex(
                    (item) => item.id === selectedSampleVoice?.id,
                  ) + 1}
                </CustomTypography>
                <ArrowRightRoundedIcon
                  sx={{
                    color: COLOR.indigo[80],
                    fontSize: '24px',
                    opacity: disableSampleAudio ? 0.4 : 1,
                  }}
                />
              </ButtonLabel>
            </Grid>
          )}

          <Grid item xs={12} sm={6}>
            <CommonInput
              label={
                <CustomTypography
                  variant="body-14-medium"
                  color={COLOR.darkGray}
                >
                  {t('voiceName')}
                </CustomTypography>
              }
              placeholder={t('enterVoiceName')}
              sx={{
                '& .MuiOutlinedInput-root': {
                  height: '40px',
                  backgroundColor: disableVoiceName
                    ? COLOR.backgroundRecordTime
                    : 'transparent',
                },
                '& .MuiInputBase-input::placeholder': {
                  color: COLOR.label2[48],
                },
              }}
              onChange={handleSelect}
              error={error.voiceName}
              helperText={error.voiceName}
              name="voiceName"
              value={voiceInfo.voiceName}
              disabled={disableVoiceName}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Select
              label={
                <CustomTypography
                  variant="body-14-medium"
                  color={COLOR.darkGray}
                >
                  {t('contentType')}
                </CustomTypography>
              }
              name="contentType"
              value={voice?.category?.id}
              placeholder={
                <CustomTypography
                  variant="body-14-regular"
                  color={COLOR.colorGrayV2}
                >
                  {t('selectContentType')}
                </CustomTypography>
              }
              disabled
              onChange={handleSelect}
              options={[voice?.category]}
              fullWidth
              getOptionLabel={(option) =>
                getCategoryLabel(option?.name, i18n.language)
              }
              customStyle={{
                height: '40px',
                borderRadius: '8px',
                border: `1px solid ${COLOR.borderButtonCorlor}`,
                width: '100%',
                fontSize: '14px',
                backgroundColor: COLOR.backgroundRecordTime,
              }}
              error={error.contentType}
              helperText={error.contentType}
              getOptionValue={(option) => option?.id}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Select
              label={
                <CustomTypography
                  variant="body-14-medium"
                  color={COLOR.darkGray}
                >
                  {t('gender')}
                </CustomTypography>
              }
              name="gender"
              value={voiceInfo.gender}
              placeholder={
                <CustomTypography
                  variant="body-14-regular"
                  color={COLOR.colorGrayV2}
                >
                  {t('selectGender')}
                </CustomTypography>
              }
              onChange={handleSelect}
              options={GENDER}
              fullWidth
              getOptionLabel={(option) => t(option.label)}
              getOptionValue={(option) => option.value}
              customStyle={{
                height: '40px',
                borderRadius: '8px',
                border: `1px solid ${COLOR.borderButtonCorlor}`,
                width: '100%',
                backgroundColor: disableGender
                  ? COLOR.backgroundRecordTime
                  : 'transparent',
                fontSize: '14px',
              }}
              error={error.gender}
              helperText={error.gender}
              disabled={disableGender}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Select
              label={
                <CustomTypography
                  variant="body-14-medium"
                  color={COLOR.darkGray}
                >
                  {t('ageGroup')}
                </CustomTypography>
              }
              name="ageGroup"
              value={Object.values(ageGroup).find(
                (option) => option === voiceInfo.ageGroup,
              )}
              placeholder={
                <CustomTypography
                  variant="body-14-regular"
                  color={COLOR.colorGrayV2}
                >
                  {t('placeHolderGroupAgeV2')}
                </CustomTypography>
              }
              disabled={disableAgeGroup}
              onChange={handleSelect}
              options={Object.values(ageGroup)}
              fullWidth
              getOptionLabel={(option) => t(option)}
              getOptionValue={(option) => option}
              error={error.ageGroup}
              helperText={error.ageGroup}
              customStyle={{
                height: '40px',
                borderRadius: '8px',
                border: `1px solid ${COLOR.borderButtonCorlor}`,
                width: '100%',
                fontSize: '14px',
                backgroundColor: disableAgeGroup
                  ? COLOR.backgroundRecordTime
                  : 'transparent',
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Select
              label={
                <CustomTypography
                  variant="body-14-medium"
                  color={COLOR.darkGray}
                >
                  {t('region')}
                </CustomTypography>
              }
              name="region"
              value={voiceInfo.region}
              placeholder={
                <CustomTypography
                  variant="body-14-regular"
                  color={COLOR.colorGrayV2}
                >
                  {t('selectRegion')}
                </CustomTypography>
              }
              disabled={disableGender}
              onChange={handleSelect}
              options={Object.values(VOICE_LOCALES)}
              fullWidth
              getOptionLabel={(option) => t(option)}
              getOptionValue={(option) => option}
              customStyle={{
                height: '40px',
                borderRadius: '8px',
                border: `1px solid ${COLOR.borderButtonCorlor}`,
                width: '100%',
                backgroundColor: disableGender
                  ? COLOR.backgroundRecordTime
                  : 'transparent',
                fontSize: '14px',
              }}
              error={error.region}
              helperText={error.region}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Select
              label={
                <CustomTypography
                  variant="body-14-medium"
                  color={COLOR.darkGray}
                >
                  {t('voiceProvince')}
                </CustomTypography>
              }
              name="province"
              value={voiceInfo?.province}
              placeholder={
                <CustomTypography
                  variant="body-14-regular"
                  color={COLOR.colorGrayV2}
                >
                  {t('selectProvince')}
                </CustomTypography>
              }
              disabled={disableGender}
              onChange={handleSelect}
              options={provinces}
              fullWidth
              getOptionLabel={(option) => t(option?.name)}
              getOptionValue={(option) => option?.id}
              customStyle={{
                height: '40px',
                borderRadius: '8px',
                border: `1px solid ${COLOR.borderButtonCorlor}`,
                width: '100%',
                backgroundColor: disableGender
                  ? COLOR.backgroundRecordTime
                  : 'transparent',
                fontSize: '14px',
              }}
              error={error.province}
              helperText={error.province}
            />
          </Grid>

          {showDescription && (
            <Grid item xs={12} sm={12}>
              <LimitedInput
                label={
                  <CustomTypography
                    variant="body-14-medium"
                    color={COLOR.darkGray}
                  >
                    {t('description')}
                  </CustomTypography>
                }
                name="description"
                value={voiceInfo?.description}
                placeholder={t('placeHolderDescription')}
                disabled={!allowEditDescription}
                onChange={handleSelect}
                error={error.description}
                helperText={error.description}
                rows={4}
                maxLength={200}
                multiline
                customStyle={{
                  backgroundColor: !allowEditDescription
                    ? COLOR.backgroundRecordTime
                    : 'transparent',
                }}
              />
            </Grid>
          )}
        </Grid>
      </Box>
      <SaveChangeButton />

      <PopupChangeVoice
        sampleAudios={sampleAudios}
        openPopupSampleAudio={openPopupSampleAudio}
        setOpenPopupSampleAudio={setOpenPopupSampleAudio}
        selectedSampleVoice={selectedSampleVoice}
        setSelectedSampleVoice={setSelectedSampleVoice}
        draftSampleVoice={draftSampleVoice}
        setDraftSampleVoice={setDraftSampleVoice}
      />
    </StyledEditVoiceInformation>
  );
};

export default NewEditVoice;
